/**
 * Cross-tree mirror of the room's comment threads for consumers outside
 * `CanvasLiveblocksProvider` (the Comments rail section).
 *
 * `useThreads` only resolves inside the RoomProvider, which is mounted in
 * `CanvasContext`; the rail lives in `WorkspaceLayout`, a sibling subtree.
 * The pin layer publishes from inside the room, exactly as `CanvasPage`
 * publishes nodes to `CanvasNodesMirrorContext`.
 *
 * `threadsSynced` is true only once Liveblocks finishes loading, so the rail
 * does not flash an empty zero state mid-load.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { ThreadPlacement } from "../comments/threadPlacement";

/** A thread reduced to what the rail needs — no comment bodies cross over. */
export type MirroredThread = {
  id: string;
  resolved: boolean;
  metadata: ThreadPlacement;
  comments: {
    id: string;
    userId: string;
    createdAt: string;
    deletedAt?: string;
  }[];
};

const EMPTY_THREADS: MirroredThread[] = [];

type CanvasCommentsMirrorState = {
  canvasId: string | undefined;
  threads: MirroredThread[];
  threadsSynced: boolean;
};

const DEFAULT_MIRROR_STATE: CanvasCommentsMirrorState = {
  canvasId: undefined,
  threads: EMPTY_THREADS,
  threadsSynced: false,
};

const CanvasCommentsMirrorThreadsContext =
  createContext<MirroredThread[]>(EMPTY_THREADS);
const CanvasCommentsMirrorSyncedContext = createContext<boolean>(false);
const CanvasCommentsMirrorCanvasIdContext = createContext<string | undefined>(
  undefined,
);

type CanvasCommentsMirrorDispatchContextValue = {
  publish: (
    update: Partial<CanvasCommentsMirrorState> & { canvasId: string },
  ) => void;
  reset: () => void;
};

const CanvasCommentsMirrorDispatchContext =
  createContext<CanvasCommentsMirrorDispatchContextValue | null>(null);

export function CanvasCommentsMirrorProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [state, setState] =
    useState<CanvasCommentsMirrorState>(DEFAULT_MIRROR_STATE);

  const publish = useCallback(
    (update: Partial<CanvasCommentsMirrorState> & { canvasId: string }) => {
      setState((prev) => ({
        canvasId: update.canvasId,
        threads: update.threads ?? prev.threads,
        threadsSynced: update.threadsSynced ?? prev.threadsSynced,
      }));
    },
    [],
  );

  const reset = useCallback(() => setState(DEFAULT_MIRROR_STATE), []);

  const dispatch = useMemo(() => ({ publish, reset }), [publish, reset]);

  return (
    <CanvasCommentsMirrorDispatchContext.Provider value={dispatch}>
      <CanvasCommentsMirrorCanvasIdContext.Provider value={state.canvasId}>
        <CanvasCommentsMirrorSyncedContext.Provider value={state.threadsSynced}>
          <CanvasCommentsMirrorThreadsContext.Provider value={state.threads}>
            {children}
          </CanvasCommentsMirrorThreadsContext.Provider>
        </CanvasCommentsMirrorSyncedContext.Provider>
      </CanvasCommentsMirrorCanvasIdContext.Provider>
    </CanvasCommentsMirrorDispatchContext.Provider>
  );
}

function useCanvasCommentsMirrorDispatch(): CanvasCommentsMirrorDispatchContextValue {
  const ctx = useContext(CanvasCommentsMirrorDispatchContext);
  if (!ctx) {
    throw new Error(
      "Canvas comments mirror hooks must be used within CanvasCommentsMirrorProvider",
    );
  }
  return ctx;
}

function guardCanvasId(
  consumerCanvasId: string | undefined,
  publishedCanvasId: string | undefined,
): boolean {
  if (!consumerCanvasId || !publishedCanvasId) return false;
  return consumerCanvasId === publishedCanvasId;
}

export function useCanvasCommentsMirror(canvasId?: string): MirroredThread[] {
  const threads = useContext(CanvasCommentsMirrorThreadsContext);
  const publishedCanvasId = useContext(CanvasCommentsMirrorCanvasIdContext);
  if (canvasId !== undefined && !guardCanvasId(canvasId, publishedCanvasId)) {
    return EMPTY_THREADS;
  }
  return threads;
}

export function useCanvasCommentsMirrorSynced(canvasId?: string): boolean {
  const synced = useContext(CanvasCommentsMirrorSyncedContext);
  const publishedCanvasId = useContext(CanvasCommentsMirrorCanvasIdContext);
  if (canvasId !== undefined && !guardCanvasId(canvasId, publishedCanvasId)) {
    return false;
  }
  return synced;
}

export function useCanvasCommentsMirrorState(canvasId?: string): {
  threads: MirroredThread[];
  threadsSynced: boolean;
} {
  return {
    threads: useCanvasCommentsMirror(canvasId),
    threadsSynced: useCanvasCommentsMirrorSynced(canvasId),
  };
}

/** Published from inside the room with the UNFILTERED thread list: the board
 *  shows unresolved pins, the rail shows every thread. */
export function usePublishCanvasCommentsMirror(
  canvasId: string | undefined,
  threads: MirroredThread[],
  threadsReady: boolean,
): void {
  const { publish, reset } = useCanvasCommentsMirrorDispatch();

  useEffect(() => {
    if (!canvasId) return;

    if (!threadsReady) {
      publish({ canvasId, threadsSynced: false });
      return;
    }

    publish({ canvasId, threads, threadsSynced: true });
  }, [threads, canvasId, threadsReady, publish]);

  useEffect(() => reset, [reset]);
}
